/* ============================================================
   CURATOR PRODUCT CARD — interactions
   ------------------------------------------------------------
   Wishlist, add to tote and quick view for the `curator-card-product`
   snippet.

   This logic used to live inside curator-collection-grid.liquid, which meant
   the card only worked inside that one section. Anywhere else the buttons
   rendered but did nothing, so the homepage In Focus row used Dawn's plain
   card instead and had no wishlist or tote button at all.

   Uses event delegation on the document rather than binding each button, so
   cards injected later (infinite scroll, section re-render, quick view) work
   with no re-initialisation.

   window.CuratorWishlist is resolved at click time, never at load time: this
   file can run before the wishlist snippet near the end of theme.liquid has
   defined it.
   ============================================================ */
(function () {
  'use strict';

  /* Several sections include this file (two product rows on the home page, the product page
     plus its rail row). Without this guard every copy attached its own document click handler,
     so one tap on a heart saved and then removed the product, and one tap on the tote icon
     added the product twice. Set up once per page. */
  if (window.__mmCuratorCardInit) return;
  window.__mmCuratorCardInit = true;

  /* ---- Cart icon refresh ----
     Adding the FIRST item used to leave the count bubble missing until the
     next page load. The bubble is rendered server side inside
     `{%- if cart != empty -%}`, so on an empty cart there is no
     .cart-count-bubble in the DOM at all, and the old code only wrote
     textContent into whatever it could find. With nothing to find, nothing
     happened, and the count appeared only once the server rendered the header
     again.

     Re-rendering sections/cart-icon-bubble.liquid through the Section
     Rendering API covers every case at once: creating the bubble on the first
     add, updating the number after that, removing it if the cart empties, and
     swapping the empty cart icon for the filled one.

     The icon and the bubble are swapped individually rather than replacing the
     whole link, because the header words the link's accessible label "Open
     tote" while the section says "Cart", and replacing wholesale would quietly
     change it for screen readers. */
  window.MMRefreshCartIcon = function () {
    return fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data['cart-icon-bubble'];
        var host = document.getElementById('cart-icon-bubble');
        if (!html || !host) return;

        var tmp = document.createElement('div');
        tmp.innerHTML = html;

        var newIcon = tmp.querySelector('.svg-wrapper');
        var oldIcon = host.querySelector('.svg-wrapper');
        if (newIcon && oldIcon) oldIcon.replaceWith(newIcon);

        var newBubble = tmp.querySelector('.cart-count-bubble');
        var oldBubble = host.querySelector('.cart-count-bubble');
        if (newBubble && oldBubble) oldBubble.replaceWith(newBubble);
        else if (newBubble) host.appendChild(newBubble);
        else if (oldBubble) oldBubble.remove();
      })
      .catch(function () {});
  };

  /* ---- Cart drawer ----
     Call after a successful add to cart. Dawn opens its drawer from
     product-form.js, which none of this theme's add to cart paths go through:
     the card button, the quick view and the product page all post to
     /cart/add.js themselves. With the drawer switched on but nothing calling
     it, adding an item would have updated the badge and nothing else.

     Rather than rewrite all five of those posts to ask for sections in the
     add request the way Dawn does, the sections are fetched here afterwards
     and handed to the drawer's own renderContents. That re-renders the drawer
     AND the cart icon and then opens it, so this one call replaces the badge
     refresh at every site.

     Falls back to refreshing just the icon when no drawer is present, so
     switching the cart type back to notification or page still works. */
  window.MMOpenCartDrawer = function (openedBy) {
    var host = document.querySelector('cart-drawer');
    if (!host || typeof host.renderContents !== 'function') {
      return window.MMRefreshCartIcon();
    }
    var ids = (typeof host.getSectionsToRender === 'function'
      ? host.getSectionsToRender().map(function (s) { return s.id; })
      : ['cart-drawer', 'cart-icon-bubble']).join(',');

    return Promise.all([
      fetch('/?sections=' + encodeURIComponent(ids)).then(function (r) { return r.json(); }),
      fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); })
    ])
      .then(function (res) {
        var sections = res[0];
        var cart = res[1];

        /* Clear is-empty on the host before rendering.

           Liquid puts the class on the <cart-drawer> element itself:
             <cart-drawer class="drawer{% if cart == empty %} is-empty{% endif %}">
           but renderContents only replaces the innerHTML of the #CartDrawer div
           inside it, and only ever clears is-empty from .drawer__inner. So on a
           cart that was empty when the page loaded, the class survived the
           re-render, and component-cart.css has

             .is-empty .cart__contents { display: none }

           which hid the entire item list. The footer uses .drawer__footer, not
           .cart__footer, so it stayed visible: the drawer opened showing the
           right total over a blank space where the products should be. */
        host.classList.toggle('is-empty', cart.item_count === 0);

        if (openedBy && typeof host.setActiveElement === 'function') {
          host.setActiveElement(openedBy);
        }
        host.renderContents({ sections: sections });
      })
      .catch(function () { return window.MMRefreshCartIcon(); });
  };

  function wishlistReady() {
    return typeof window.CuratorWishlist !== 'undefined' && window.CuratorWishlist;
  }

  /* ---- Wishlist ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.curator-card__wishlist-btn');
    if (!btn) return;
    e.preventDefault();
    if (!wishlistReady()) return;

    var added = window.CuratorWishlist.toggle({
      id: btn.getAttribute('data-product-id'),
      variant_id: btn.getAttribute('data-variant-id'),
      handle: btn.getAttribute('data-product-handle'),
      title: btn.getAttribute('data-product-title'),
      url: btn.getAttribute('data-product-url'),
      image: btn.getAttribute('data-product-image'),
      price: btn.getAttribute('data-product-price'),
      compare_at_price: btn.getAttribute('data-product-compare-price')
    });
    btn.classList.toggle('is-wishlisted', !!added);
  });

  /* ---- Add to tote ----
     A product with variants is never added blind. The shopper is sent to the
     product page to choose, because picking a size on their behalf is how you
     end up shipping the wrong one. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.curator-card__cart-btn');
    if (!btn) return;
    e.preventDefault();

    if (btn.getAttribute('data-has-variants') === 'true') {
      /* Needs a size or colour chosen first. Prefer the quick view modal so the
         shopper can pick without losing their place; only fall back to a full
         navigation where no modal exists. Silently jumping to the product page
         is what made this button feel broken. */
      var card = btn.closest('.curator-card');
      var handle = btn.getAttribute('data-product-handle') ||
                   (card && card.getAttribute('data-product-handle'));
      if (handle && typeof window.CuratorQuickView === 'function') {
        window.CuratorQuickView(handle);
        return;
      }
      var url = (card && card.getAttribute('data-product-url')) || btn.getAttribute('data-product-url');
      if (url) window.location.href = url;
      return;
    }

    var vid = btn.getAttribute('data-variant-id');
    if (btn.getAttribute('data-available') !== 'true' || !vid) return;

    btn.style.opacity = '1';
    btn.style.color = '#27ae60';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        setTimeout(function () { btn.style.color = ''; btn.style.opacity = ''; }, 1200);
        return window.MMOpenCartDrawer(btn);
      })
      .catch(function () { btn.style.color = ''; btn.style.opacity = ''; });
  });

  /* ---- Quick view ----
     The modal lives in curator-collection-grid.liquid, which publishes
     window.CuratorQuickView. Elsewhere there is no modal, so fall back to
     opening the product page. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.curator-card__quick-view-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-product-handle');
    if (handle && typeof window.CuratorQuickView === 'function') {
      window.CuratorQuickView(handle);
      return;
    }
    var url = btn.getAttribute('data-product-url');
    if (url) window.location.href = url;
  });

  /* Reflect saved state on cards as they appear. */
  function syncHearts(root) {
    if (!wishlistReady()) return;
    (root || document).querySelectorAll('.curator-card__wishlist-btn').forEach(function (btn) {
      var h = btn.getAttribute('data-product-handle');
      if (h) btn.classList.toggle('is-wishlisted', window.CuratorWishlist.isInWishlist(h));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { syncHearts(); });
  } else {
    syncHearts();
  }

  document.addEventListener('wishlist:changed', function () { syncHearts(); });

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType === 1 && n.querySelector && n.querySelector('.curator-card__wishlist-btn')) {
            syncHearts(n);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
